import React, { useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { notify } from '../../../utils/notification';
import { decideDefenderReaction } from '../utils/combatSaveStateService';
import { mergeSpeciesResources } from '../../../shared/rules/speciesResources.mjs';

export const STONES_ENDURANCE = 'species:stones-endurance';

/**
 * Stone's Endurance (2024 Goliath, Stone Giant) when an attack hits: use the Reaction to reduce the damage by 1d12 +
 * Constitution modifier, spending a Giant Ancestry use. A Monk's Deflect Attacks prompt handles offers that include it.
 */
export default function StonesEndurancePrompt({ campaignId, characterId, combatState, onCombatChange, onCharacterChange }) {
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const offer = (combatState?.reactionOpportunities || []).find((o) => o.status === 'pending' && String(o.targetCombatantId) === String(characterId)
    && o.candidateFeatures?.includes(STONES_ENDURANCE) && !o.candidateFeatures.includes('monk:deflect-attacks') && !o.candidateFeatures.includes('spell:shield'));
  const decide = async (decision, featureId = STONES_ENDURANCE) => {
    if (lock.current || !offer) return;
    lock.current = true; setBusy(true);
    try {
      const result = await decideDefenderReaction(campaignId, offer.opportunityId, decision, featureId);
      onCombatChange?.(result.combat);
      if (result.speciesResources) onCharacterChange?.((previous) => mergeSpeciesResources(previous, result.speciesResources));
      const used = result.opportunity;
      if (used?.reduction !== undefined) notify(`Stone's Endurance: ${used.reductionRoll} (d12) + ${used.constitutionModifier} CON = ${used.reduction} damage reduction.`, 'success');
    } catch (error) { notify(error.message || 'Reaction could not be saved.', 'danger'); }
    finally { lock.current = false; setBusy(false); }
  };
  return (
    <Modal show={Boolean(offer)} centered backdrop="static" keyboard={false} className="dnd-modal modern-modal" aria-label="Stone's Endurance">
      <Modal.Header><Modal.Title>Stone&apos;s Endurance</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{offer?.rawDamage ? `An attack hit you for ${offer.rawDamage} damage.` : 'An attack hit you.'}</p>
        <p>Use your Reaction and a Giant Ancestry use to reduce the damage by 1d12 + your Constitution modifier?</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => decide('skip')}>Skip</Button>
        {offer?.candidateFeatures?.includes('rogue:uncanny-dodge') && (
          <Button variant="outline-light" disabled={busy} onClick={() => decide('use', 'rogue:uncanny-dodge')}>Uncanny Dodge instead</Button>
        )}
        {offer?.candidateFeatures?.includes('ranger:hunter:superior-hunters-defense') && (
          <Button variant="outline-light" disabled={busy} onClick={() => decide('use', 'ranger:hunter:superior-hunters-defense')}>Superior Hunter's Defense instead</Button>
        )}
        <Button disabled={busy} onClick={() => decide('use')}>{busy ? 'Saving…' : "Use Stone's Endurance"}</Button>
      </Modal.Footer>
    </Modal>
  );
}
